import { subject } from "@casl/ability";
import { useAbility } from "@casl/react";
import { addDays } from "date-fns";
import { Plus } from "lucide-react";
import { type FC, useState } from "react";

import type { AppAbility } from "@recobra/core";
import { m } from "@recobra/i18n/messages";
import { OPEN_TASK_STATUSES } from "@recobra/schemas/task";
import { taskCalendarDate, type Task, type TaskReminder } from "@recobra/schemas/task";
import { Badge } from "@recobra/ui/badge";
import { Button } from "@recobra/ui/button";
import {
  ItemGroup,
  Item,
  ItemContent,
  ItemTitle,
  ItemDescription,
  ItemActions,
} from "@recobra/ui/item";

import { useAuth } from "../../lib";
import { TaskModalSection, formatTaskDate } from "../task-display";
import { TaskReminderRow } from "./TaskReminderRow";

export const TaskRemindersSection: FC<{ task: Task }> = ({ task }) => {
  const ability = useAbility<AppAbility>();
  const { user } = useAuth();
  const [newReminders, setNewReminders] = useState<TaskReminder[]>([]);
  const taskSubject = subject("Task", task);
  const isOpen = OPEN_TASK_STATUSES.includes(task.status);
  const canUpdate = ability.can("updateReminder", taskSubject) && isOpen;
  const canRemove = ability.can("removeReminder", taskSubject) && isOpen;
  const canAdd = ability.can("createReminder", taskSubject) && isOpen;
  const savedIds = new Set(task.reminders.map((reminder) => reminder.id));
  const reminders = [
    ...[...task.reminders].sort((left, right) =>
      left.scheduledDate.localeCompare(right.scheduledDate),
    ),
    ...newReminders.filter((reminder) => !savedIds.has(reminder.id)),
  ];
  if (!canAdd && reminders.length === 0) return null;

  return (
    <TaskModalSection>
      {canAdd ? (
        <div className="flex justify-end">
          <Button
            type="button"
            size="xs"
            variant="outline"
            onClick={() => setNewReminders((current) => [...current, blankReminder(user.username)])}
          >
            <Plus data-icon="inline-start" />
            {m.task_reminders_add()}
          </Button>
        </div>
      ) : null}

      {isOpen ? (
        <ul className="flex flex-col gap-1">
          {reminders.map((reminder) => (
            <TaskReminderRow
              key={`${reminder.id}:${reminder.status}`}
              task={task}
              reminder={reminder}
              isNew={newReminders.some((candidate) => candidate.id === reminder.id)}
              canUpdate={canUpdate}
              canRemove={canRemove}
              onDiscardNew={() =>
                setNewReminders((current) =>
                  current.filter((candidate) => candidate.id !== reminder.id),
                )
              }
            />
          ))}
        </ul>
      ) : (
        <ItemGroup>
          {reminders.map((reminder) => (
            <Item key={reminder.id} size="xs">
              <ItemContent>
                <ItemTitle>
                  <time dateTime={reminder.scheduledDate}>
                    {formatTaskDate(reminder.scheduledDate)}
                  </time>
                </ItemTitle>
                {reminder.comment ? <ItemDescription>{reminder.comment}</ItemDescription> : null}
              </ItemContent>
              <ItemActions>
                <Badge variant="secondary">
                  {m.task_reminders_status_label({ status: reminder.status })}
                </Badge>
              </ItemActions>
            </Item>
          ))}
        </ItemGroup>
      )}
    </TaskModalSection>
  );
};

const blankReminder = (username: string): TaskReminder => ({
  id: crypto.randomUUID(),
  scheduledDate: taskCalendarDate(addDays(new Date(), 1)),
  status: "pending",
  createdBy: username,
  createdAt: new Date().toISOString(),
});
